/* eslint-disable no-nested-ternary */
import React, { useMemo } from 'react';
import styled from 'styled-components';
import moment from 'moment';
import { DATE_FORMAT } from 'utils';
import { formatEventMonthDay } from 'utils/dateTime';
import { useGlobalDateLocale } from 'context/DateLocaleContext';
import useEventBookingSession from 'features/Events/hooks/useEventBookingSession';
import { SkeletonLine } from 'components/theme/Skeleton/Skeleton';
import { areSessionsFree, filterNonBlockedSessions } from 'utils/session';
import { useTranslation } from 'react-i18next';
import useWaitList from 'api/waitlist/useWaitList';
import { WaitlistMode } from '@kouto/types';
import { PageTitle } from 'components/atoms/PageTitle';
import useLimitedTimeStatus, {
  TicketSalesState,
} from 'hooks/useLimitedTimeStatus';
import LimitedTimeMessage from 'components/atoms/LimitedTime/LimitedTimeMessage';
import { Brand } from 'api/brand/fetchBrandById';
import BuyTicketButton, {
  BuyTicketButtonState,
  getDateStateFromSessionsAvailability,
} from './BuyTicketButton';

interface EventDetailsHeaderProps {
  readonly promoterBrand?: Brand;
}

const EventDetailsHeader = ({ promoterBrand }: EventDetailsHeaderProps) => {
  const { t } = useTranslation();
  const { locale } = useGlobalDateLocale();
  const { isLoading, listing, preselectedDate, eventSessions } =
    useEventBookingSession();
  const { data: waitlist } = useWaitList(listing?.waitlistId, listing?.brandId);
  const waitlistEnabled = !!waitlist && waitlist?.mode !== WaitlistMode.NONE;

  const limitedTime = useLimitedTimeStatus(
    listing?.limitedTime?.ticketSalesOpenAt,
    listing?.limitedTime?.ticketSalesCloseAt,
  );

  const firstAvailableDate = useMemo(() => {
    const today = moment().format(DATE_FORMAT);
    if (preselectedDate && preselectedDate >= today) {
      return preselectedDate;
    }

    return listing?.eventDates
      ? listing.eventDates
          .filter((date) => date === today || date > today)
          .sort((d1, d2) => (d1 > d2 ? 1 : -1))[0]
      : today;
  }, [listing, preselectedDate]);

  const { buttonState, buttonDate } = useMemo(() => {
    const nonBlocked = filterNonBlockedSessions(eventSessions);
    const today = moment().format(DATE_FORMAT);
    const futureDates = (listing?.eventDates ?? [])
      .filter((d) => d >= today)
      .sort();

    // Find the first date that still has available sessions
    const bookableDate = futureDates.find(
      (date) =>
        getDateStateFromSessionsAvailability(
          nonBlocked,
          date,
          waitlistEnabled,
        ) !== BuyTicketButtonState.PASSED,
    );

    if (bookableDate) {
      return {
        buttonState: getDateStateFromSessionsAvailability(
          nonBlocked,
          bookableDate,
          waitlistEnabled,
        ),
        buttonDate: bookableDate,
      };
    }

    // All dates passed
    return {
      buttonState: getDateStateFromSessionsAvailability(
        nonBlocked,
        firstAvailableDate,
        waitlistEnabled,
      ),
      buttonDate: firstAvailableDate,
    };
  }, [eventSessions, listing?.eventDates, firstAvailableDate, waitlistEnabled]);

  const firstDateLabel = useMemo(
    () => formatEventMonthDay(buttonDate ?? firstAvailableDate ?? '', locale),
    [buttonDate, firstAvailableDate, locale],
  );

  if (isLoading || !listing) {
    return (
      <Wrapper>
        {listing?.title && <PageTitle translatedTitle={listing?.title} />}
        <FirstDate>
          <SkeletonLine style={{ marginBottom: 0 }} />
        </FirstDate>
        <EventTitle>
          <SkeletonLine style={{ marginBottom: 0 }} />
        </EventTitle>
        <BuyTicketButtonSkeleton />
      </Wrapper>
    );
  }

  const isFree = areSessionsFree(eventSessions, buttonDate);
  const isClosed = limitedTime?.state === TicketSalesState.CLOSED;
  const isComingSoon = limitedTime?.state === TicketSalesState.NOT_YET_OPEN;

  return (
    <Wrapper>
      {!!limitedTime?.countdown && (
        <LimitedTimeText>
          <LimitedTimeMessage
            label={limitedTime.countdown.full}
            highlightPart={limitedTime.countdown.highlight}
          />
        </LimitedTimeText>
      )}

      {listing?.title && <PageTitle translatedTitle={listing?.title} />}
      <FirstDate className="event_details_first_date">
        {firstDateLabel}
      </FirstDate>
      <EventTitle
        className="event_details_title"
        promoterBrand={!!promoterBrand?.name}
      >
        {listing.title}
      </EventTitle>
      {promoterBrand?.name && (
        <BrandName>
          {t('by')} {promoterBrand.name}
        </BrandName>
      )}
      {isClosed ? null : (
        <BuyTicketButton
          className="event_details_header_buy_ticket_button"
          date={buttonDate}
          state={buttonState}
          isFree={isFree}
          customLabel={isComingSoon ? t('comingSoon') : undefined}
          disabled={isComingSoon}
        />
      )}
    </Wrapper>
  );
};

const Wrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  flex-direction: column;
  padding: 16px;
`;

const FirstDate = styled.p`
  margin: 0px;
  font-weight: 700;
`;

const EventTitle = styled.h2<{ promoterBrand?: boolean }>`
  text-align: center;
  min-width: 60%;
  font-size: 56px;
  margin-bottom: ${({ promoterBrand }) => (promoterBrand ? '11px' : '')};
  @media screen and (max-width: 768px) {
    min-width: 90%;
    font-size: 40px;
  }
`;

export const BrandName = styled.p`
  color: var(--Text-Tertiary, rgba(0, 0, 0, 0.6));
  font-size: 16px;
  font-style: normal;
  font-weight: 400;
  line-height: normal;
  letter-spacing: 0.016px;
  margin-top: 0px;
  margin-bottom: 24px;
`;

const BuyTicketButtonSkeleton = styled(SkeletonLine)`
  height: 40px;
  width: 120px;
  margin-bottom: 0px;
`;

const LimitedTimeText = styled.div`
  margin-bottom: 16px;
`;

export default EventDetailsHeader;
